import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, ExternalLink, Calendar, Building2,
  Globe, CheckCircle2, Loader2, ArrowRight, ChevronLeft, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { supabase } from '../lib/supabase';
import SEO from '../components/SEO';

const ReferenceDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [reference, setReference] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    loadReference();
  }, [slug]);

  const loadReference = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('our_references')
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .maybeSingle();

      if (error || !data) {
        navigate('/references');
        return;
      }
      setReference(data);
    } catch (err) {
      console.error('Error loading reference:', err);
      navigate('/references');
    } finally {
      setLoading(false);
    }
  };

  const texts = {
    tr: {
      backToReferences: 'Referanslara Dön',
      client: 'Müşteri',
      completedDate: 'Tamamlanma Tarihi',
      technologies: 'Kullanılan Teknolojiler',
      services: 'Verilen Hizmetler',
      visitSite: 'Siteyi Ziyaret Et',
      results: 'Sonuçlar',
      gallery: 'Proje Görselleri',
      getQuote: 'Teklif Al',
      ctaTitle: 'Benzer bir proje mi planlıyorsunuz?',
      ctaDesc: 'Sizin için de aynı başarıyı yakalayalım.'
    },
    en: {
      backToReferences: 'Back to References',
      client: 'Client',
      completedDate: 'Completion Date',
      technologies: 'Technologies Used',
      services: 'Services Provided',
      visitSite: 'Visit Site',
      results: 'Results',
      gallery: 'Project Gallery',
      getQuote: 'Get Quote',
      ctaTitle: 'Planning a similar project?',
      ctaDesc: 'Let us achieve the same success for you.'
    }
  };

  const t = texts[language] || texts.tr;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!reference) return null;

  const title = language === 'tr' ? reference.title_tr : reference.title_en;
  const excerpt = language === 'tr' ? reference.excerpt_tr : reference.excerpt_en;
  const content = language === 'tr' ? reference.content_tr : reference.content_en;
  const technologies = reference.technologies || [];
  const servicesUsed = reference.services_used || [];
  const metrics = reference.metrics || {};
  const galleryImages = reference.gallery_images || [];

  const allImages = reference.featured_image_url
    ? [reference.featured_image_url, ...galleryImages]
    : galleryImages;

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + allImages.length) % allImages.length);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950" data-testid="reference-detail-page">
      <SEO
        title={`${title} - Başarı Hikayesi`}
        description={excerpt || `${reference.client_name} için hazırladığımız ${title} projesini inceleyin.`}
        keywords={`${reference.client_name}, ${title}, referans, proje, başarı hikayesi, cplus media`}
      />

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-gray-900 via-indigo-900 to-purple-900 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
          >
            <Link
              to="/references"
              className="inline-flex items-center text-gray-300 hover:text-white mb-8 transition-colors"
              data-testid="back-to-references"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t.backToReferences}
            </Link>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center gap-3 mb-4">
                {reference.client_logo_url && (
                  <div className="w-12 h-12 bg-white rounded-lg p-2">
                    <img
                      src={reference.client_logo_url}
                      alt={reference.client_name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <span className="text-gray-300 font-medium">{reference.client_name}</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                {title}
              </h1>

              <p className="text-xl text-gray-300 mb-8">
                {excerpt}
              </p>

              <div className="flex flex-wrap items-center gap-4">
                {reference.completed_at && (
                  <div className="flex items-center gap-2 text-gray-400">
                    <Calendar className="w-5 h-5" />
                    <span>{new Date(reference.completed_at).toLocaleDateString(language === 'tr' ? 'tr-TR' : 'en-US', { year: 'numeric', month: 'long' })}</span>
                  </div>
                )}
                {reference.project_url && (
                  <a
                    href={reference.project_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-white/10 text-white rounded-lg hover:bg-white/20 transition-colors"
                    data-testid="visit-site-btn"
                  >
                    <Globe className="w-4 h-4 mr-2" />
                    {t.visitSite}
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </a>
                )}
              </div>
            </motion.div>

            {/* Featured Image */}
            {allImages.length > 0 && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="relative"
              >
                <div className="relative aspect-video rounded-2xl overflow-hidden shadow-2xl">
                  <img
                    src={allImages[currentImageIndex]}
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                  {allImages.length > 1 && (
                    <>
                      <button
                        onClick={prevImage}
                        className="absolute left-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                        data-testid="gallery-prev"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        onClick={nextImage}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                        data-testid="gallery-next"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                        {allImages.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setCurrentImageIndex(idx)}
                            className={`w-2 h-2 rounded-full transition-colors ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'
                              }`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>

      {/* Content Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="lg:col-span-2"
          >
            {content && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8 mb-8">
                <div
                  className="prose dark:prose-invert max-w-none"
                  dangerouslySetInnerHTML={{ __html: content.replace(/\n/g, '<br/>') }}
                />
              </div>
            )}

            {/* Metrics/Results */}
            {Object.keys(metrics).length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                  {t.results}
                </h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {Object.entries(metrics).map(([key, value], idx) => (
                    <div key={idx} className="text-center p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                      <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-1">
                        {value}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {key}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="space-y-6"
          >
            {/* Client Info */}
            <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
              <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t.client}</h3>
              <div className="flex items-center gap-3">
                {reference.client_logo_url && (
                  <div className="w-12 h-12 bg-gray-100 dark:bg-gray-800 rounded-lg p-2">
                    <img
                      src={reference.client_logo_url}
                      alt={reference.client_name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
                <span className="font-bold text-gray-900 dark:text-white">{reference.client_name}</span>
              </div>
            </div>

            {/* Technologies */}
            {technologies.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t.technologies}</h3>
                <div className="flex flex-wrap gap-2">
                  {technologies.map((tech, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1 text-sm font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Services Used */}
            {servicesUsed.length > 0 && (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-2xl p-6">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">{t.services}</h3>
                <div className="space-y-2">
                  {servicesUsed.map((service, idx) => (
                    <Link
                      key={idx}
                      to={`/solutions/${service}`}
                      className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                    >
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <span className="capitalize">{service.replace(/-/g, ' ')}</span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-3xl p-12 text-center">
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              {t.ctaTitle}
            </h2>
            <p className="text-indigo-100 mb-8 max-w-2xl mx-auto">
              {t.ctaDesc}
            </p>
            <Link
              to="/portal/new-quote"
              className="inline-flex items-center px-8 py-4 bg-white text-indigo-600 rounded-xl font-bold hover:shadow-xl hover:-translate-y-1 transition-all"
              data-testid="detail-cta-btn"
            >
              {t.getQuote}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ReferenceDetail;
